import { z } from 'zod';

/**
 * How a campaign is doing (docs/36 §8.7): its leads, its calls, its agents, and what it cost.
 * Everything is counted from the calls the campaign recorded (dial_attempts), with talk time and
 * cost from the call records they produced (cdrs.attempt_id).
 */

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const CampaignAnalyticsQuerySchema = z.object({
  /** The account's own days, inclusive. Both left out is the campaign's whole life. */
  from: Day.optional(),
  to: Day.optional(),
}).strict();

export interface CampaignAnalytics {
  range: { from: string | null; to: string | null; tz: string };
  /** Where the leads are now, whatever the range. */
  leads: { total: number; reached: number; not_reached: number; do_not_call: number; remaining: number };
  calls: {
    total: number;
    /** Somebody (or something) answered: answered, transferred, message, no key, machine. */
    answered: number;
    /** A person was reached: answered, transferred, message, no key. */
    connected: number;
    by_outcome: Array<{ outcome: string; n: number }>;
    talk_sec: number;
  };
  dispositions: Array<{ code: string; label: string; effect: string | null; n: number }>;
  agents: Array<{
    extension_id: string; name: string; calls: number; connected: number; talk_sec: number;
    /** Average from the call ending to the result being saved (preview campaigns). */
    wrap_sec: number | null;
    /** Calls given a final disposition. */
    final: number;
  }>;
  /** Answering-machine decisions by result and engine; `charged` is what the AI checks cost (0135). */
  amd: Array<{ result: string; engine: string | null; n: number; charged: string | null }>;
  by_hour: Array<{ hour: number; calls: number; connected: number }>;
  by_day: Array<{ day: string; calls: number; connected: number; reached: number }>;
  /** Reached contacts by how many calls it took. */
  calls_to_reach: Array<{ calls: number; n: number }>;
  /** What the calls were charged, when the reader may see call charges. */
  cost: { amount: number; currency: string | null } | null;
}

/** Every campaign side by side over one range (Reporting -> Campaigns). */
export interface CampaignReportRow {
  id: string; name: string; mode: string; status: string; created_at: string;
  leads: number; reached: number; remaining: number;
  calls: number; connected: number; final: number; talk_sec: number;
  /** Null when the reader may not see call charges. */
  cost: number | null; currency: string | null;
}
